import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getBwCdrSummary, uploadBwCdr, reconcile, generateInvoice, listInvoices, getInvoice,
  type Reconcile, type InvoiceDetail,
} from "../api/invoicing";

const money = (n: number) => n.toLocaleString(undefined, { style: "currency", currency: "USD" });
const fmtTs = (s: string | null) => (s ? new Date(s).toLocaleString() : "—");

function BwCdrCard() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["bw-cdr-summary"], queryFn: getBwCdrSummary, refetchInterval: 15000 });
  const [msg, setMsg] = useState<string | null>(null);
  const up = useMutation({
    mutationFn: (f: File) => uploadBwCdr(f),
    onSuccess: (r) => { setMsg(r.message); qc.invalidateQueries({ queryKey: ["bw-cdr-summary"] }); },
    onError: (e) => setMsg(`Upload failed: ${(e as Error).message}`),
  });
  return (
    <div className="bg-white rounded-lg shadow p-4">
      <h2 className="text-lg font-semibold mb-1">Bandwidth CDR data</h2>
      <p className="text-xs text-gray-500 mb-3">Upload the weekly Bandwidth Call Detail Records CSV. Loaded in the background and used as the authoritative basis for reconciliation and invoicing.</p>
      <div className="text-sm text-gray-700 space-y-1 mb-3">
        <div><span className="text-gray-500">Rows loaded:</span> {(data?.rows ?? 0).toLocaleString()}</div>
        <div><span className="text-gray-500">Coverage:</span> {fmtTs(data?.earliest ?? null)} → {fmtTs(data?.latest ?? null)}</div>
      </div>
      <input type="file" accept=".csv" disabled={up.isPending}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) up.mutate(f); e.target.value = ""; }}
        className="text-sm" />
      {up.isPending && <span className="ml-2 text-sm text-gray-500">Uploading…</span>}
      {msg && <p className="text-sm mt-2 text-gray-600">{msg}</p>}
      {!!data?.uploads?.length && (
        <div className="mt-3">
          <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">Recent uploads</div>
          <table className="w-full text-xs">
            <tbody className="divide-y divide-gray-100">
              {data.uploads.map((u, i) => (
                <tr key={i}>
                  <td className="py-1 pr-2 truncate max-w-[180px]">{u.filename}</td>
                  <td className="py-1 px-2 text-right">{u.rows_loaded.toLocaleString()} loaded</td>
                  <td className="py-1 pl-2 text-gray-400">{fmtTs(u.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ReconcileGenerate() {
  const qc = useQueryClient();
  const [usrId, setUsrId] = useState(7);
  const [ps, setPs] = useState("2026-05-16");
  const [pe, setPe] = useState("2026-05-23");
  const [rec, setRec] = useState<Reconcile | null>(null);
  const [genMsg, setGenMsg] = useState<string | null>(null);
  const recM = useMutation({ mutationFn: () => reconcile(usrId, ps, pe), onSuccess: setRec, onError: (e) => setGenMsg((e as Error).message) });
  const genM = useMutation({
    mutationFn: () => generateInvoice(usrId, ps, pe),
    onSuccess: (r: any) => { setGenMsg(`Generated ${r.invoice_number}: ${money(r.total)} (${r.calls?.toLocaleString?.() ?? "?"} calls, ${r.mode})`); qc.invalidateQueries({ queryKey: ["invoices"] }); },
    onError: (e) => setGenMsg(`Generate failed: ${(e as Error).message}`),
  });
  return (
    <div className="bg-white rounded-lg shadow p-4">
      <h2 className="text-lg font-semibold mb-3">Reconcile &amp; generate</h2>
      <div className="flex flex-wrap gap-3 items-end mb-3">
        <label className="text-sm">User ID
          <input type="number" value={usrId} onChange={(e) => setUsrId(Number(e.target.value))}
            className="block w-24 border border-gray-300 rounded px-2 py-1.5 text-sm" /></label>
        <label className="text-sm">Period start
          <input type="date" value={ps} onChange={(e) => setPs(e.target.value)}
            className="block border border-gray-300 rounded px-2 py-1.5 text-sm" /></label>
        <label className="text-sm">Period end
          <input type="date" value={pe} onChange={(e) => setPe(e.target.value)}
            className="block border border-gray-300 rounded px-2 py-1.5 text-sm" /></label>
        <button onClick={() => recM.mutate()} disabled={recM.isPending}
          className="bg-gray-100 hover:bg-gray-200 rounded px-3 py-2 text-sm disabled:opacity-50">
          {recM.isPending ? "Reconciling…" : "Reconcile"}</button>
        <button onClick={() => genM.mutate()} disabled={genM.isPending}
          className="bg-brand hover:bg-brand-dark text-white rounded px-3 py-2 text-sm disabled:opacity-50">
          {genM.isPending ? "Generating…" : "Generate invoice"}</button>
      </div>
      {rec && (
        <div className="grid grid-cols-2 gap-3 text-sm border-t border-gray-100 pt-3">
          <div className="bg-gray-50 rounded p-3">
            <div className="text-xs text-gray-400 uppercase">Bandwidth (carrier)</div>
            <div className="text-xl font-bold">{rec.bandwidth.connected_calls.toLocaleString()} <span className="text-sm font-normal text-gray-500">calls</span></div>
            <div className="text-gray-500">{rec.bandwidth.raw_minutes.toLocaleString()} raw min</div>
          </div>
          <div className="bg-gray-50 rounded p-3">
            <div className="text-xs text-gray-400 uppercase">Our records (CDR)</div>
            <div className="text-xl font-bold">{rec.ours.answered_calls.toLocaleString()} <span className="text-sm font-normal text-gray-500">calls</span></div>
            <div className="text-gray-500">{rec.ours.minutes.toLocaleString()} min · captured {rec.captured_pct ?? "—"}%</div>
          </div>
          <p className="col-span-2 text-xs text-gray-400">{rec.note}</p>
        </div>
      )}
      {genMsg && <p className="text-sm mt-2 text-gray-700">{genMsg}</p>}
    </div>
  );
}

function InvoiceView({ id, onClose }: { id: number; onClose: () => void }) {
  const { data, isLoading } = useQuery({ queryKey: ["invoice", id], queryFn: () => getInvoice(id) });
  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-20 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        {isLoading || !data ? <p className="p-6 text-gray-500">Loading…</p> : <InvoiceBody d={data} />}
        <div className="p-4 border-t flex justify-end gap-2">
          <button onClick={() => window.print()} className="bg-gray-100 hover:bg-gray-200 rounded px-3 py-1.5 text-sm">Print / PDF</button>
          <button onClick={onClose} className="bg-brand text-white rounded px-3 py-1.5 text-sm">Close</button>
        </div>
      </div>
    </div>
  );
}

function InvoiceBody({ d }: { d: InvoiceDetail }) {
  // Derived, all from the real line data (no fabricated figures).
  const callLines = d.lines.filter((l) => l.type === "calls");
  const usageMin = callLines.reduce((s, l) => s + l.quantity, 0);
  const effRate = usageMin > 0 ? d.subtotal / usageMin : 0;          // genuine blended $/min
  const didLine = d.lines.find((l) => l.type === "did");
  const balLine = d.lines.find((l) => l.type === "balance_forward");
  const didAmt = didLine?.amount ?? 0;
  const didCount = didLine ? Math.round(didLine.quantity) : 0;
  const balAmt = balLine?.amount ?? 0;
  // Pull a call count out of the descriptions ("(N calls)") for the volume stat.
  const callCount = callLines.reduce((s, l) => {
    const m = l.description.match(/([\d,]+)\s+calls?/i);
    return s + (m ? parseInt(m[1].replace(/,/g, ""), 10) : 0);
  }, 0);

  const rateCell = (l: InvoiceDetail["lines"][number]) =>
    l.type === "usf" ? "30%"
      : l.type === "did" ? "$0.25/mo"
        : l.type === "balance_forward" ? "—"
          : `$${l.unit_rate.toFixed(6)}/min`;
  const qtyCell = (l: InvoiceDetail["lines"][number]) =>
    l.type === "usf" || l.type === "balance_forward" ? "—"
      : l.type === "did" ? `${l.quantity.toLocaleString()} #`
        : `${l.quantity.toLocaleString(undefined, { maximumFractionDigits: 0 })} min`;

  return (
    <div className="p-0 bg-white">
      {/* Letterhead */}
      <div className="flex justify-between items-start px-8 py-6 text-white" style={{ background: "linear-gradient(90deg,#2563eb,#1d4ed8)" }}>
        <div>
          <div className="text-xl font-extrabold">Reliadata.io</div>
          <div className="text-xs font-medium opacity-80">Wholesale multi-channel communications</div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-extrabold tracking-widest">INVOICE</div>
          <div className="font-mono text-sm opacity-95">#{d.invoice_number}</div>
          <span className="inline-block mt-1 text-[11px] uppercase bg-white/20 rounded-full px-2 py-0.5">{d.status}</span>
        </div>
      </div>

      {/* Bill-to + period */}
      <div className="flex justify-between px-8 py-4 border-b text-sm">
        <div>
          <div className="text-[11px] text-gray-400 uppercase">Billed to</div>
          <b className="text-gray-800">{d.company || d.username}</b>
          <div className="text-gray-500">{d.email}</div>
        </div>
        <div className="text-right">
          <div className="text-[11px] text-gray-400 uppercase">Service period</div>
          <div className="text-gray-800">{d.period_start} – {d.period_end}</div>
        </div>
      </div>

      {/* Value summary band — genuine metrics from this invoice's own usage */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-gray-100 border-b">
        <Metric label="Calls carried" value={callCount ? callCount.toLocaleString() : "—"} />
        <Metric label="Minutes used" value={usageMin.toLocaleString(undefined, { maximumFractionDigits: 0 })} />
        <Metric label="Your blended rate" value={`$${effRate.toFixed(4)}`} sub="/min" accent />
        <Metric label="Numbers in service" value={didCount ? didCount.toLocaleString() : "—"} />
      </div>

      {/* Line items */}
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-gray-400 text-[11px] uppercase bg-gray-50">
            <th className="px-8 py-2">Description</th>
            <th className="px-4 py-2 text-right">Qty</th>
            <th className="px-4 py-2 text-right">Rate</th>
            <th className="px-8 py-2 text-right">Amount</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {d.lines.map((l, i) => (
            <tr key={i} className={l.type === "balance_forward" ? "bg-amber-50/40" : ""}>
              <td className="px-8 py-2 text-gray-700">{l.description}</td>
              <td className="px-4 py-2 text-right tabular-nums text-gray-500">{qtyCell(l)}</td>
              <td className="px-4 py-2 text-right tabular-nums text-gray-500">{rateCell(l)}</td>
              <td className="px-8 py-2 text-right tabular-nums">{money(l.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Truthful value note — covertly reassures without inventing numbers */}
      <div className="mx-8 my-4 rounded-md bg-blue-50 border border-blue-100 px-4 py-3 text-[12px] text-blue-900 leading-relaxed">
        <b>How your rate is calculated.</b> Every call is rated individually at our
        wholesale NPA-NXX rates, with the lower of the interstate/intrastate rate applied
        per call — so you pay the most favorable applicable rate on each one. Your blended
        effective rate this period was <b>${effRate.toFixed(4)}/min</b>. Regulatory fees are
        assessed on usage only — <b>your phone numbers are never taxed</b>.
      </div>

      {/* Totals */}
      <div className="ml-auto w-96 px-8 pb-6 text-sm">
        <div className="flex justify-between py-1"><span className="text-gray-600">Usage (calls)</span><span className="tabular-nums">{money(d.subtotal)}</span></div>
        <div className="flex justify-between py-1"><span className="text-gray-600">USF / taxes / reg. fees <span className="text-gray-400">(usage only)</span></span><span className="tabular-nums">{money(d.usf_fee)}</span></div>
        {didLine && <div className="flex justify-between py-1"><span className="text-gray-600">Number rental <span className="text-gray-400">(no tax)</span></span><span className="tabular-nums">{money(didAmt)}</span></div>}
        {balLine && <div className="flex justify-between py-1"><span className="text-gray-600">Balance forward</span><span className="tabular-nums">{money(balAmt)}</span></div>}
        <div className="flex justify-between pt-3 mt-2 border-t-2 border-gray-800 text-lg font-extrabold text-brand-dark"><span>Total due</span><span className="tabular-nums">{money(d.total)}</span></div>
      </div>

      <div className="px-8 pb-6 text-center text-[11px] text-gray-400 border-t pt-4">
        Thank you for your business. Questions about this invoice? Reply to your account team — we'll walk through any line with you.
      </div>
    </div>
  );
}

function Metric({ label, value, sub, accent }: { label: string; value: string; sub?: string; accent?: boolean }) {
  return (
    <div className="bg-white px-4 py-3">
      <div className={`text-xl font-bold tabular-nums ${accent ? "text-brand" : "text-gray-800"}`}>
        {value}{sub && <span className="text-xs font-medium text-gray-400 ml-0.5">{sub}</span>}
      </div>
      <div className="text-[10px] uppercase tracking-wide text-gray-400">{label}</div>
    </div>
  );
}

export function InvoicingPage() {
  const { data: invoices } = useQuery({ queryKey: ["invoices", undefined], queryFn: () => listInvoices(undefined) });
  const [viewId, setViewId] = useState<number | null>(null);
  return (
    <div>
      <h1 className="text-2xl font-bold mb-4">Invoicing &amp; Reconciliation</h1>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <BwCdrCard />
        <ReconcileGenerate />
      </div>
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">Invoices</h2>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-gray-500">
            <tr><th className="px-4 py-2">Invoice</th><th className="px-4 py-2">User</th><th className="px-4 py-2">Period</th>
              <th className="px-4 py-2 text-right">Subtotal</th><th className="px-4 py-2 text-right">USF</th>
              <th className="px-4 py-2 text-right">Total</th><th className="px-4 py-2">Status</th><th className="px-4 py-2"></th></tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {(invoices ?? []).map((inv) => (
              <tr key={inv.billing_run_id}>
                <td className="px-4 py-2 font-mono text-xs">{inv.invoice_number}</td>
                <td className="px-4 py-2">#{inv.usr_id}</td>
                <td className="px-4 py-2">{inv.period_start} – {inv.period_end}</td>
                <td className="px-4 py-2 text-right tabular-nums">{money(inv.subtotal)}</td>
                <td className="px-4 py-2 text-right tabular-nums">{money(inv.usf_fee)}</td>
                <td className="px-4 py-2 text-right tabular-nums font-semibold">{money(inv.total)}</td>
                <td className="px-4 py-2">{inv.status}</td>
                <td className="px-4 py-2 text-right"><button onClick={() => setViewId(inv.billing_run_id)} className="text-brand hover:underline">View</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        {!invoices?.length && <p className="text-gray-400 text-sm p-4">No invoices yet.</p>}
      </div>
      {viewId && <InvoiceView id={viewId} onClose={() => setViewId(null)} />}
    </div>
  );
}
